import React from 'react';
import PropTypes from 'prop-types';
import Hotkeys from 'react-hot-keys';

import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Slider from '@mui/material/Slider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { yellow } from '@mui/material/colors';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {postCall, getCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {validId} from '../helpers/validate';
import history from '../helpers/history';
import {ROOT_PATH} from '../settings';
import Autocomplete from '../component/Autocomplete';
import ProgressSpinner from '../component/atoms/ProgressSpinner';
import ResearcherId from '../component/molecule/ResearcherId';
import {checkUserRoles} from '../helpers/authentication';
import Organization from '../component/molecule/Organization';
import ProfileDetails from '../component/organism/ProfileDetails';
import DocumentStats from '../component/molecule/DocumentStats';
import NameVariants from '../component/molecule/NameVariants';
import CurationProfileIgnoreButton from './CurationProfileIgnoreButton';
import CurationProfileMergeButton from './CurationProfileMergeButton';
import HotKeyChip from '../component/atoms/HotKeyChip';
import CustomAccordion from '../component/molecule/CustomAccordion';
import Box from "@mui/material/Box";


const StyledSearchResult = styled('div')({
    marginTop: 30,
    marginBottom: 30
});

const StyledSearchResultHeader = styled(Grid)(({ theme }) =>({
    padding: '10px 41px 10px 16px',
    marginTop: 10,
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: theme.custom.font.bold
}));

const StyledSearchResultItem = styled(Grid)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledFormTitle = styled(Typography)({
    marginBottom: 20,
    fontWeight: 500,
    fontSize: 20
});

const StyledGridRow = styled(Grid)({
    marginBottom: 15
});

const StyledSliderDefault = styled(Slider)({
    height: 0,
    padding: '40px 0 0 0',
    marginLeft: -20,
    '& .MuiSlider-track': {
        height: 8,
        borderRadius: 4
    },
    '& .MuiSlider-thumb': {
        height: 40,
        width: 40,
        backgroundColor: '#ffffff',
        border: '2px solid currentColor',
        marginTop: 0,
        marginLeft: 0,
        '&:focus, &:hover, &$active': {
            boxShadow: 'inherit'
        }
    },
    '& .MuiSlider-valueLabel': {
        left: 'calc(-50%+10px)',
        top: 31,
        backgroundColor: 'transparent',
        '& *': {
            color: '#000000',
            fontSize: '12px'
        }
    },
    '& .MuiSlider-rail': {
        height: 8,
        borderRadius: 4
    }
});

const StyledSliderDisabled = styled(StyledSliderDefault)({
      '& .MuiSlider-track': {
          color: '#e0e0e0'
      },
      '& .MuiSlider-thumb': {
          borderColor: '#e0e0e0'
      },
      '& .MuiSlider-valueLabel': {
          '& *': {
              color: '#e0e0e0'
          }
      },
        '& .MuiSlider-rail': {
          backgroundColor: '#e0e0e0'
      }
});

const StyledFormFab = styled(Fab)({
    height: '48px',
    width: '48px'
});

const StyledFormSepText = styled(Typography)(({ theme }) =>({
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize,
    lineHeight: theme.custom.font.line_height,
    paddingTop: '100%',
    fontWeight: theme.custom.font.bold
}));

const StyledErrorMessage = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 30,
    marginBottom: 30,
    textAlign: 'center'
}));

const StyledWarningRoundedIcon = styled(WarningRoundedIcon)({
    verticalAlign: 'text-bottom',
    marginLeft: 5,
    color: yellow[700]
});

const StyledAccordionDetails = styled('div')(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));

const StyledTooltip = styled(Tooltip)(({ theme }) =>({
    fontSize: theme.custom.font.medium
}));


class CurationProfileMerge extends React.Component {

    constructor(props) {
        super(props);

        this.default_state = {
            last_name: this.props.last_name || '',
            first_name: this.props.first_name + '*' || '',
            search_id: '',
            search_city: '',
            city_id: '',
            search_org: '',
            grid_id: '',
            co_names: '',
            possible_min_year: undefined,
            possible_max_year: undefined,
            selected_min_year: undefined,
            selected_max_year: undefined,
            filter_min_year: undefined,
            filter_max_year: undefined,
            filter_year_range_active: true,
            curation_profile: {},
            search_is_running: false,
            profiles: null,
            result_count: 0,
            result_to_big_info: '',
            take_over_curation_profile_id: undefined,
            form_errors: {},
            profile_data: {},
            next_page: undefined
        };

        this.state = this.default_state;

        this.form_data = {};

        this.is_mounted = false;
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.search_profile_url = concatUrl(window.ROOT_URL, 'api', 'profile', 'search');
        this.search_city_url = concatUrl(window.ROOT_URL, 'api', 'completion', 'search_city');
        this.search_org_url = concatUrl(window.ROOT_URL, 'api', 'completion', 'search_org');
        this.load_profile_data_url = concatUrl(window.ROOT_URL, 'api', 'profile', 'session_data');
        this.researcher_base_data_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'base_data');

        // max year number offset that is added to define the max. possible lower end of the year filter
        this.year_filter__max_offset_lower_end = 30;
        // max year number offset that is added to define the max. possible upper end of the year filter
        this.year_filter__max_offset_upper_end = 30;
        // year number offset that is added to set the default filter value on the lower end
        this.year_filter__default_offset_lower_end = 10;
        //  year number offset that is added to set the default filter value on the upper end
        this.year_filter__default_offset_upper_end = 10;
    }

    componentDidMount(){
        this.is_mounted = true;
        this.context.checkUserAccess(this.conditionCallback);
        if (this.props.curation_profile_id && this.props.workspace_id){
            this.loadCurationProfileDetails(this.props.curation_profile_id);
        }
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    componentDidUpdate(prevProps, prevState){
        if(this.props.curation_profile_id !== prevProps.curation_profile_id){
            this.setState(this.default_state);
        }
        if (prevState.next_page !== this.state.next_page && this.state.next_page){
            this.register_lazy_loading();
        }
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    stopPropagation = (event) => {
        event.stopPropagation();
    };

    register_lazy_loading = () => {
        let options = {
          root: null,
          threshold: 0.5
        };
        if (this.state.next_page){
            let observer = new IntersectionObserver(this.loadNextPage, options);
            let target = document.querySelector('#nextPage');
            observer.observe(target);
        }
    };

    loadNextPage = (entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && this.state.next_page && !this.state.loading_next_page) {
                this.loadNextSearchResults();
            }
        });
    };

    removeProfileFromList = (profile_id) => {
        // remove (a merged, ignored) profile from the current result list.
        this.setState({
            profiles: this.state.profiles.filter(function(el) { return el.profile_id !== parseInt(profile_id) }),
            result_count: this.state.result_count - 1
        });
    };

    mergeProfileCallback = (profile_id) => {
        if(this.props.current_profile_id){
            // merge profile into an existing profile
            this.removeProfileFromList(profile_id);
            this.props.mergeCallback(this.props.curation_profile_id);
        }else{
            // initial merge
            window.location.href = `/workspaces/curation_profiles/details?id=${this.props.curation_profile_id}&workspace_id=${this.props.workspace_id}`;
        }
    };

    // +++ load curation profile data +++++++++++++++++++++++++++++++++++++++++++++++
    // required to set min and max year in slider
    loadCurationProfileDetailsFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load curation profile details.');
    };

    loadCurationProfileDetailsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else {
            if(data.doc_stats){
                let current_year =  new Date().getFullYear();

                // set min_year and max_year defaults
                let min_year = undefined;
                let max_year = undefined;

                // set min_year and max_year based on the profile documents
                if (data.doc_stats.grant_min_year !== null){
                    min_year = data.doc_stats.grant_min_year;
                }
                if (data.doc_stats.publication_min_year !== null && (data.doc_stats.publication_min_year < min_year || min_year == null)){
                    min_year = data.doc_stats.publication_min_year;
                }

                if (data.doc_stats.grant_max_year !== null){
                    max_year = data.doc_stats.grant_max_year;
                }
                if (data.doc_stats.publication_max_year !== null && (data.doc_stats.publication_max_year > max_year || max_year == null)){
                    max_year = data.doc_stats.publication_max_year;
                }

                // set min_year and max_year to current year in case it's not set yet
                if (min_year === undefined){
                    min_year = current_year;
                }
                if (max_year === undefined){
                    max_year = current_year;
                }

                // add filter offset
                let filter_min_year = min_year - this.year_filter__default_offset_lower_end;
                let possible_min_year = min_year - this.year_filter__max_offset_lower_end;

                let filter_max_year = max_year + this.year_filter__default_offset_upper_end;
                let possible_max_year = max_year + this.year_filter__max_offset_upper_end;

                // restrict upper filter in case it goes beyond current year
                if (filter_max_year > current_year + 1){
                    filter_max_year = current_year + 1;
                }
                if (possible_max_year > current_year + 1){
                    possible_max_year = current_year + 1;
                }
                
                this.setState({
                    filter_min_year: filter_min_year,
                    selected_min_year: filter_min_year,
                    possible_min_year: possible_min_year,
                    filter_max_year: filter_max_year,
                    selected_max_year: filter_max_year,
                    possible_max_year: possible_max_year
                });
            }
        }
    };

    loadCurationProfileDetails = (curation_profile_id) => {
        getCall(`${this.researcher_base_data_url}?id=${curation_profile_id}&workspace_id=${this.props.workspace_id}`,
            this.loadCurationProfileDetailsDone,
            this.loadCurationProfileDetailsFailed);
    };

    // +++ search profile +++++++++++++++++++++++++++++++++++++++++++++++

    searchProfileFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.setState({
            search_is_running: false
        });
        this.context.displayGlobalError('Backend service failure: Search profile is not working.');
    };

    searchProfileDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data && data.form_error){
            this.setState({
                form_errors: {search: data.form_error},
                search_is_running: false
            });
        }else {
            if (data) {
                if (data.profiles) {
                    let result_count = data.result_count;
                    let result_to_big_info = '';
                    if (data.result_count > data.max_result_count){
                        result_count = data.max_result_count;
                        result_to_big_info = `More than ${data.max_result_count} results found. Please specify your search.`;
                    }
                    this.setState({
                        profiles: data.profiles,
                        result_count: result_count,
                        result_to_big_info: result_to_big_info,
                        next_page: data.next_page,
                        search_is_running: false
                    });
                }
            } else {
                callback_failure();
            }
        }
    };

    searchProfile = () => {
        if(this.state.last_name.trim() === '' && this.state.first_name.trim() === '' && this.state.search_id.trim() !== '' && !validId(this.state.search_id.trim())){
            this.setState({form_errors: {search: 'Invalid researcher ID'}});
        }else{
            this.setState({
                form_errors: {},
                profiles: null,
                search_is_running: true
            });
            this.form_data = {
                current_profile_id: this.props.current_profile_id,
                curation_profile_id: this.props.curation_profile_id,
                search_id: this.state.search_id.trim(),
                last_name: this.state.last_name.trim(),
                first_name: this.state.first_name.trim(),
                city_id: this.state.city_id,
                grid_id: this.state.grid_id,
                co_names: this.state.co_names.trim(),
                year_range_active: this.state.filter_year_range_active,
                min_year: this.state.filter_min_year,
                max_year: this.state.filter_max_year,
            };
            postCall(this.search_profile_url, this.form_data, this.searchProfileDone, this.searchProfileFailed);
        }
    };

    // +++ load next search results +++++++++++++++++++++++++++++++++++++++++++++++

    loadNextSearchResultsFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load next page.');
        this.setState({loading_next_page: false});
    };

    loadNextSearchResultsDone = (data, callback_failure) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data) {
            callback_failure();
        }else{
            let profiles = this.state.profiles;
            this.setState({
                profiles: profiles.concat(data.profiles),
                next_page: data.next_page,
                loading_next_page: false
            });
        }
    };

    loadNextSearchResults = () => {
        this.setState({loading_next_page: true});
        getCall(this.state.next_page, this.loadNextSearchResultsDone, this.loadNextSearchResultsFailed);
    };

    // +++ load profile data +++++++++++++++++++++++++++++++++++++++++++++++

    loadProfileDataFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.context.displayGlobalError('Backend service failure: Could not load profile data.');
    };

    loadProfileDataDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (!data){
            this.context.displayGlobalError('Backend service failure: Could not load profile data.');
        }else{
            if (data && data.no_access){
                history.push(ROOT_PATH);
            } else {
                let profile_data = this.state.profile_data;
                profile_data[data.profile_id] = data.profile_data;
                this.setState({
                    profile_data: profile_data
                });
            }
        }
    };

    loadProfileData = (profile_id) => {
        getCall(this.load_profile_data_url + '?profile_id=' + profile_id + '&curation_profile_id=' + this.props.curation_profile_id, this.loadProfileDataDone, this.loadProfileDataFailed);
    };

    handleExpansionPanelChange = profile_id => () => {
        if(!this.state.profile_data[profile_id]){
            // load data
            this.loadProfileData(profile_id);
        }
    };

    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <StyledErrorMessage>
                    {this.state.form_errors[key]}
                </StyledErrorMessage>
            );
        }
    };

    topOneOrganization = (profile_data) => {
        if(profile_data && profile_data.current_organization){
            return <Organization organization={profile_data.current_organization} profileListStyle={true}/>;
        }
    };

    isCurationProfileInfo = (is_curation_profile) => {
        if(is_curation_profile){
            return (
                <StyledTooltip title="This profile is already a curation profile!" placement="right">
                    <StyledWarningRoundedIcon fontSize="small" />
                </StyledTooltip>
            );
        }
    };

    layoutTooltip = (tooltip) => {
        return (<div>
            {tooltip.split('\n').map((line, _index) => <div key={_index}>
                {line}
            </div>)}
        </div>);
    };

    layoutTooltips = (tooltips) => {
        if(tooltips.length === 1){
            return this.layoutTooltip(tooltips[0]);
        }
        return (
            <div>
                {tooltips.map((tooltip, index) => <div key={index}>
                    {this.layoutTooltip(tooltip)}
                    <br/>
                </div>)}
            </div>
        );
    };

    accordanceInformation = (profile) => {
        if(profile.accordance) {
            return (
                <StyledTooltip title={this.layoutTooltips(profile.accordance.requirements_descriptions)} placement="right">
                    <span>
                        {profile.accordance.display_score}
                        {profile.accordance.requirements_missed_level >= 1 && <StyledWarningRoundedIcon fontSize="small" />}
                    </span>
                </StyledTooltip>
            );
        }
    };

    profileList(){
        if(this.state.profiles){
            return (
                <div>
                    {this.state.profiles.map((profile) => <CustomAccordion
                        key={profile.profile_id}
                        onOpenCallback={this.handleExpansionPanelChange(profile.profile_id)}
                        summary={
                            <Box sx={{ flexGrow: 1 }}>
                                <StyledSearchResultItem container spacing={2}>
                                    <Grid size={3}>
                                        {profile.full_name}
                                        {this.isCurationProfileInfo(profile.is_curation_profile)}
                                        <NameVariants variants={profile.name_variants} />
                                    </Grid>
                                    <Grid size={1}>
                                        {this.accordanceInformation(profile)}
                                    </Grid>
                                    <Grid size={3}>
                                        {this.topOneOrganization(profile)}
                                    </Grid>
                                    <Grid size={2}>
                                        <DocumentStats profile={profile} />
                                    </Grid>
                                    <Grid size={2}>
                                        <ResearcherId id={profile.researcher_id} />
                                    </Grid>
                                    <Grid size={1}>
                                        <CurationProfileMergeButton
                                            profile={profile}
                                            curation_profile_id={this.props.curation_profile_id}
                                            current_profile_id={this.props.current_profile_id}
                                            workspace_id={this.props.workspace_id}
                                            doneCallback={this.mergeProfileCallback}
                                        />
                                        <CurationProfileIgnoreButton
                                            profile={profile}
                                            curation_profile_id={this.props.curation_profile_id}
                                            workspace_id={this.props.workspace_id}
                                            doneCallback={this.removeProfileFromList}
                                        />
                                    </Grid>
                                </StyledSearchResultItem>
                            </Box>
                        }
                        content={
                            <StyledAccordionDetails key={profile.profile_id}>
                                <ProfileDetails profile={this.state.profile_data[profile.profile_id]} includeCoAuthorNames={true} />
                            </StyledAccordionDetails>}
                    />)}
                </div>
            );
        }
    }

    searchResult() {
        if(this.state.search_is_running){
            return <ProgressSpinner />;
        }
        if (this.state.profiles){
            let count_info = !this.state.result_to_big_info ? `${this.state.result_count} profiles found` : this.state.result_to_big_info;
            return (
                <StyledSearchResult>
                    <Typography variant="h6">
                        {count_info}
                    </Typography>
                    <StyledSearchResultHeader container spacing={2}>
                        <Grid size={3}>
                            Name
                        </Grid>
                        <Grid size={1}>
                            Similarity<br/>
                            score
                        </Grid>
                        <Grid size={3}>
                            Organization <br/>
                            City, State, Country
                        </Grid>
                        <Grid size={2}>
                            Publications <br/>
                            Grants
                        </Grid>
                        <Grid size={2}>
                            Researcher ID
                        </Grid>
                        <Grid size={1} />
                    </StyledSearchResultHeader>
                    {this.profileList()}
                    {this.state.next_page && <div id="nextPage"><ProgressSpinner /></div>}
                </StyledSearchResult>
            );
        }
    }

    onCitySelect = (suggestion) => {
        this.setState({
            city_id: suggestion.geonames_id,
            form_errors: {}
        });
    };

    resetCitySelection = () => {
        this.setState({
            city_id: '',
            form_errors: {}
        });
    };

    selectionRequiredCallback = () => {
        this.setState({
            form_errors: {search: "You have to select one of the suggestions."}
        });
    };

    autoCompleteSearchFailedCallback = () => {
        this.context.displayGlobalError('Backend service failure: Could not run auto complete.');
    };

    onOrgSelect = (suggestion) => {
        this.setState({
            grid_id: suggestion.id,
            form_errors: {}
        });
    };

    resetOrgSelection = () => {
        this.setState({
            grid_id: '',
            form_errors: {}
        });
    };

    errorMode = (key) => {
        return key in this.state.form_errors && this.state.form_errors[key] !== null;
    };

    profileSearch() {

        const noFormData = (!this.state.last_name.trim() || !this.state.first_name.trim()) && !this.state.search_id.trim();
        const conflictingFormData = (this.state.last_name.trim() !== '' || this.state.first_name.trim() !== '') && this.state.search_id.trim() !== '';
        const searchButtonDisabled = noFormData || conflictingFormData;
        let StyledSlider = this.state.filter_year_range_active ? StyledSliderDefault : StyledSliderDisabled;

        return (
            <div>
                <StyledFormTitle>
                    Search profiles
                </StyledFormTitle>
                <form>
                    <StyledGridRow container spacing={5}>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="last_name"
                                    type="text"
                                    label="Last name"
                                    value={this.state.last_name}
                                    error={this.errorMode('last_name')}
                                    onChange={(event) => {
                                        this.setState({last_name: event.target.value});
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="first_name"
                                    type="text"
                                    label="First name"
                                    value={this.state.first_name}
                                    onChange={(event) => {
                                        this.setState({first_name: event.target.value});
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid>
                            <StyledFormSepText>
                                OR
                            </StyledFormSepText>
                        </Grid>
                        <Grid size={2}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="search_id"
                                    type="text"
                                    label="Researcher ID"
                                    placeholder="ur.1234567.89"
                                    value={this.state.search_id}
                                    onChange={(event) => {
                                        this.setState({search_id: event.target.value});
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size="3">
                            <StyledFormFab aria-label="add" color="primary"
                                 onClick={this.searchProfile} disabled={searchButtonDisabled}>
                                <SearchIcon />
                            </StyledFormFab>
                            <HotKeyChip hotKey="alt+s" />
                            <Hotkeys keyName="alt+s" onKeyDown={this.searchProfile} />
                        </Grid>
                    </StyledGridRow>
                    <StyledGridRow container spacing={5}>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <Autocomplete id="search_city"
                                              label="City"
                                              getSuggestionsUrl={this.search_city_url}
                                              onSuggestionSelected={this.onCitySelect}
                                              onChange={this.resetCitySelection}
                                              selectionRequired={true}
                                              selectionRequiredCallback={this.selectionRequiredCallback}
                                              autoCompleteSearchFailedCallback={this.autoCompleteSearchFailedCallback}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <Autocomplete id="search_org"
                                              label="Organization"
                                              getSuggestionsUrl={this.search_org_url}
                                              onSuggestionSelected={this.onOrgSelect}
                                              onChange={this.resetOrgSelection}
                                              selectionRequired={true}
                                              selectionRequiredCallback={this.selectionRequiredCallback}
                                              autoCompleteSearchFailedCallback={this.autoCompleteSearchFailedCallback}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <TextField
                                    variant="outlined"
                                    id="co_names"
                                    type="text"
                                    label="Co-Authors"
                                    placeholder="lastname, initials; lastname, initials; ..."
                                    value={this.state.co_names}
                                    onChange={(event) => {
                                        this.setState({co_names: event.target.value});
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid size="3"></Grid>
                    </StyledGridRow>
                    <StyledGridRow container spacing={5}>
                        <Grid size={3}>
                            <FormControl fullWidth={true}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={this.state.filter_year_range_active}
                                            onChange={() => {
                                                this.setState({filter_year_range_active: !this.state.filter_year_range_active});
                                            }}
                                            name="filter_year_range_active"
                                            color="primary"
                                        />
                                    }
                                    label="Include year range filter"
                                />
                            </FormControl>
                        </Grid>
                        <Grid size={6}>
                             <FormControl fullWidth={true}>
                                {this.state.selected_min_year && this.state.selected_max_year && <StyledSlider
                                    track="normal"
                                    aria-labelledby="year-range-slider"
                                    min={this.state.possible_min_year}
                                    max={this.state.possible_max_year}
                                    defaultValue={[this.state.selected_min_year, this.state.selected_max_year]}
                                    valueLabelDisplay="on"
                                    onChange={(_, value) => {
                                        this.setState({
                                            filter_min_year: value[0],
                                            filter_max_year: value[1]
                                        });
                                    }}
                                />}
                            </FormControl>
                        </Grid>
                        <Grid size="3"></Grid>
                    </StyledGridRow>
                </form>
                {this.showErrorMessage('search')}
                {this.searchResult()}
            </div>
        );
    }

    render() {
        return (
            <GlobalContext.Consumer>{() => (
                <div>
                    {this.profileSearch()}
                </div>
            )}
            </GlobalContext.Consumer>
        );
    }
}

CurationProfileMerge.contextType = GlobalContext;

CurationProfileMerge.propTypes = {
    curation_profile_id: PropTypes.number.isRequired,
    workspace_id: PropTypes.number.isRequired,
    last_name: PropTypes.string,
    first_name: PropTypes.string,
    current_profile_id: PropTypes.number,
    mergeCallback: PropTypes.func
};

export default CurationProfileMerge;
